import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import secureAxios from "../../utils/secureAxios";

const EditBranchForm = ({ branchId, onDone }) => {
  const [formData, setFormData] = useState({
    branch_name: "",
    description: "",
    customer_id: "",
    is_active: true,
  });
  const [customers, setCustomers] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const { user } = useContext(AuthContext);
  const tenant_id = user?.tenant_id;

  useEffect(() => {
    if (!branchId || !tenant_id) return;

    setInitialLoading(true);

    // Load branch data
    secureAxios
      .get(`/api/branches/${branchId}?tenant_id=${tenant_id}`)
      .then((res) => {
        const data = res.data;
        setFormData({
          branch_name: data.branch_name || "",
          description: data.description || "",
          customer_id: data.customer_id || "",
          is_active: data.is_active !== false,
        });
      })
      .catch((err) => {
        console.error("Error loading branch:", err);
        setMessage("Failed to load branch data");
      })
      .finally(() => {
        setInitialLoading(false);
      });

    // Load customers
    secureAxios
      .get(`/api/customers?tenant_id=${tenant_id}`)
      .then((res) => {
        setCustomers(res.data);
      })
      .catch((err) => {
        console.error("Error loading customers:", err);
      });
  }, [branchId, tenant_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);

    try {
      if (!formData.branch_name.trim()) {
        setMessage("Branch/Office name is required");
        setLoading(false);
        return;
      }

      const payload = {
        branch_name: formData.branch_name.trim(),
        description: formData.description.trim(),
        customer_id: formData.customer_id || null,
        is_active: formData.is_active,
        tenant_id,
        updated_by: user?.user_id,
      };

      await secureAxios.put(`/api/branches/${branchId}`, payload);

      setMessage("Branch/Office updated successfully!");

      if (onDone) {
        setTimeout(() => onDone(), 1500);
      }
    } catch (err) {
      console.error("Error updating branch:", err);
      const backendMessage =
        err?.response?.data?.error || "Failed to update branch";
      setMessage(backendMessage);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading branch data...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="row g-3">
      {message && (
        <div className="col-12">
          <div
            className={`alert ${
              message.includes("successfully")
                ? "alert-success"
                : "alert-danger"
            }`}
          >
            {message}
          </div>
        </div>
      )}

      <div className="col-12 col-md-6">
        <label className="form-label">
          Branch/Office Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className="form-control"
          name="branch_name"
          value={formData.branch_name}
          onChange={handleChange}
          placeholder="e.g., Downtown Office, North Region"
          required
          disabled={loading}
          maxLength="100"
        />
        <div className="form-text">
          This name will appear in the "Bill To" section on invoices
        </div>
      </div>

      <div className="col-12 col-md-6">
        <label className="form-label">Customer (Optional)</label>
        <select
          className="form-select"
          name="customer_id"
          value={formData.customer_id}
          onChange={handleChange}
          disabled={loading}
        >
          <option value="">All Customers (Global)</option>
          {customers.map((c) => (
            <option key={c.customer_id} value={c.customer_id}>
              {c.name}
            </option>
          ))}
        </select>
        <div className="form-text">
          Leave blank to make this branch available for all customers
        </div>
      </div>

      <div className="col-12">
        <label className="form-label">Description (Optional)</label>
        <textarea
          className="form-control"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Brief description of this branch or office location"
          rows="2"
          disabled={loading}
          maxLength="255"
        />
      </div>

      <div className="col-12">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="is_active"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            disabled={loading}
          />
          <label className="form-check-label" htmlFor="is_active">
            Active
          </label>
          <div className="form-text">
            Inactive branches will not appear in dropdown selections
          </div>
        </div>
      </div>

      <div className="col-12">
        {/* Mobile: Stack buttons vertically */}
        <div className="d-flex flex-column flex-sm-row gap-2">
          <button
            type="submit"
            className="btn btn-success order-1 order-sm-1"
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Save Changes
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary order-2 order-sm-2"
            onClick={onDone}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

export default EditBranchForm;